import axios from 'axios';

import { businessConstants } from '../constants';
import { assembleBaseline } from '../../redux/actions/assemblyActions';
import { organizationActions } from "./organizationAction";
import { periodsActions } from "./periodsActions";
import { accountActions } from "./accountActions";
import { entityActions } from "./entityActions";
import { reportingActions } from "./reportingActions";
import { buildCalculationAccountStructure } from '../../utils/accountStructure/accountStructureUtils';

const setSelectedBusinessCaseId = (id) => ({
  type: 'BUSINESS_SET_SELECTED_ID',
  payload: id,
});

const clearSelectedBusinessCaseId = () => ({
  type: 'BUSINESS_CLEAR_SELECTED_ID',
});

const clearAssembledBusinessCases = () => ({
  type: 'CLEAR_ASSEMBLED_BUSINESS_CASES',
});

export const businessActions = {
	getBusinessCases,
	createBusinessCase,
	updateBusinessCase,
	deleteBusinessCase,
	assembleBusinessCase,
	assembleBusinessCaseTotalsBatch,
	clearAssembledBusinessCase,
	clearAssembledBusinessCases,
	setSelectedBusinessCaseId,
	clearSelectedBusinessCaseId,
  checkBcWarmStatus,
  checkAllBcWarmStatuses,
	
};

function normalizeAccNo(x) {
  return String(x ?? "").trim(); // optionally .toUpperCase()
}

function buildAccountNumberToId(accountStructure) {
  const accountNumberToId = new Map();

  if (!accountStructure) {
    return accountNumberToId;
  }

   const addTree = (nodes) => {
    (nodes || []).forEach((row) => {
      // Separators are display-only.
      if (
        row?.row_type === 'separator' ||
        row?.separator_id != null
      ) {
        return;
      }

      const account = row?.account ?? row;

      const accountNumber =
        account?.account_number;

      const accountId =
        account?.account_id;

      if (
        accountNumber &&
        accountId != null
      ) {
        accountNumberToId.set(
          normalizeAccNo(accountNumber),
          Number(accountId),
        );
      }

      if (Array.isArray(row?.children)) {
        addTree(row.children);
      }
    });
  };

  addTree(accountStructure?.balancesheet?.assets);
  addTree(accountStructure?.balancesheet?.liabilities);
  addTree(accountStructure?.balancesheet?.equities);
  addTree(accountStructure?.profitloss);
  addTree(accountStructure?.cashflow);
  addTree(accountStructure?.drivers);

    (accountStructure?.custom || []).forEach(
    (customTab) => {
      addTree(
        customTab?.custom_tab_accounts,
      );
    },
  );

  return accountNumberToId;
}

function remapToId(mapByNumber, accountNumberToId, normalize = (x) => String(x ?? "")){
  const byId = new Map();
  if (!(mapByNumber instanceof Map)) return byId;

  for (const [accNo, vals] of mapByNumber.entries()) {
    const accId = accountNumberToId.get(normalize(accNo));
    if (accId != null) byId.set(accId, vals);
  }
  return byId;
}

function getBusinessCases() {
	return async (dispatch) => {
		dispatch(request());

		try {
			const response = await axios.get('/business-cases');

			if (response.status === 200) dispatch(success(response.data));
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to update account structure at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: businessConstants.GET_BUSINESS_CASE_REQUEST };
	}
	function success(businessCases) {
		return {
			type: businessConstants.GET_BUSINESS_CASE_SUCCESS,
			businessCases,
		};
	}
	function failure(error) {
		return { type: businessConstants.GET_BUSINESS_CASE_FAILURE, error };
	}
}

function createBusinessCase(name, scenarioNodes) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const response = await axios.post('/business-cases', {
				name: name,
				scenarioIDs: scenarioNodes,
			});

			if (response.status !== 201) {
				throw new Error(`Unexpected status ${response.status}`);
			}
			
			const newBusinessCase = response.data;
			
			// Update list in store
			const current = getState().business.businessCases ?? [];
      		const next = [...current, newBusinessCase];
      		dispatch(success(next));

      		// return the created BC so callers can await it
      	return newBusinessCase;
	} catch (error) {
		const message =
        error?.response?.data?.toString?.() ??
        'Unable to create business case at this moment. Please try again later.';
		
		dispatch(failure(message));

      // Re-throw so callers using await/try-catch can handle failures
      throw error;
    }
};

	function request() {
		return { type: businessConstants.CREATE_BUSINESS_CASE_REQUEST };
	}
	function success(businessCases) {
		return {
			type: businessConstants.CREATE_BUSINESS_CASE_SUCCESS,
			businessCases,
		};
	}
	function failure(error) {
		return { type: businessConstants.CREATE_BUSINESS_CASE_FAILURE, error };
	}
}

function updateBusinessCase(businessCaseId, name, scenarioIds, allocations) {
	return async (dispatch, getState) => {
		dispatch(request());   

		try {
			const url = '/business-cases/' + businessCaseId;
			const response = await axios.put(url, { name, scenarioIds });

			if (response.status === 200) {
				const updatedBusinessCase = response.data;
				const newBusinessCaseStructure = [...getState().business.businessCases];

				const UpdatedBusinessCaseStructure = newBusinessCaseStructure.map(
					(businessCase) => {
						return businessCase.business_case_id ===
							updatedBusinessCase.business_case_id
							? updatedBusinessCase
							: businessCase;
					},
				);

				dispatch(success(UpdatedBusinessCaseStructure));
        dispatch({ type: businessConstants.CLEAR_ASSEMBLED_BUSINESS_CASE, payload: businessCaseId });
        return updatedBusinessCase;
			}

			dispatch(failure(response.data?.toString?.() ?? "Update failed."));
      return false;
    } catch (error) {
      console.error(error);

      if (error.response) {
        dispatch(failure(error.response.data.toString()));
      } else {
        dispatch(
          failure("Unable to update account structure at this moment. Please try again later.")
        );
      }

      // return falsy value on failure
      return false;
    }
  };

	function request() {
		return { type: businessConstants.UPDATE_BUSINESS_CASE_REQUEST };
	}
	function success(businessCases) {
		return {
			type: businessConstants.UPDATE_BUSINESS_CASE_SUCCESS,
			businessCases,
		};
	}
	function failure(error) {
		return { type: businessConstants.UPDATE_BUSINESS_CASE_FAILURE, error };
	}
}

function deleteBusinessCase(businessCase) {
  return async (dispatch) => {
    dispatch({ type: businessConstants.DELETE_BUSINESS_CASE_REQUEST });

    try {
      const response = await axios.delete("/business-cases", {
        data: { id: businessCase.business_case_id },
      });

      if (response.status === 200) {
        dispatch({
          type: businessConstants.DELETE_BUSINESS_CASE_SUCCESS,
          businessCases: response.data,
        });

        // refresh reporting structure so stale BC references are removed 
       
        dispatch(reportingActions.getReportingStructure({force: true}));

        //  Also clear assembled totals (helps avoid stale charts)
        dispatch({ type: businessConstants.CLEAR_ASSEMBLED_BUSINESS_CASES });

        return true;
      }

      dispatch({
        type: businessConstants.DELETE_BUSINESS_CASE_FAILURE,
        error: response.data?.toString?.() ?? "Delete failed.",
      });
      return false;
    } catch (error) {
      dispatch({
        type: businessConstants.DELETE_BUSINESS_CASE_FAILURE,
        error:
          error?.response?.data?.toString?.() ??
          error?.message ??
          "Unable to delete business case at this moment. Please try again later.",
      });
      return false;
    }
  };
}


function assembleBusinessCase(businessCaseId, allocations, opts = {}) {
  const { force = false } = opts;

  return async (dispatch, getState) => {
 let state = getState();

  // If called too early (common on navigation), load prereqs first.
  if (!state.organization.organization?.expanded_periods) {
    await dispatch(organizationActions.getOrganization());
    state = getState();
  }

  if (!Array.isArray(state.periods.periods) || state.periods.periods.length === 0) {
    await dispatch(periodsActions.getPeriods());
    state = getState();
  }

  if (!state.accounts.accountStructure) {
    await dispatch(accountActions.getAccountStructure());
    state = getState();
  }

  

  if (!state.entities.entities) {
    await dispatch(entityActions.getEntities());
    state = getState();
  }

  if (!Array.isArray(state.business.businessCases) || state.business.businessCases.length === 0) {
    await dispatch(businessActions.getBusinessCases());
    state = getState();
  }

  const organization = state.organization.organization;
  const displayAccountStructure =
    state.accounts.accountStructure;

  const calculationAccountStructure =
    buildCalculationAccountStructure(
      displayAccountStructure,
    );
  const entities = state.entities.entities;
  const periods = state.periods.periods;
  const businessCases = state.business.businessCases;

  
    // guards (keep exactly like you have)
    if (!Array.isArray(businessCases) || businessCases.length === 0) {
      console.warn("[assembleBusinessCase] blocked: businessCases not loaded", businessCases);
      dispatch(failure("Business cases not loaded yet."));
      return;
    }

    const businessCase = businessCases.find(
      (bCase) => Number(bCase.business_case_id) === Number(businessCaseId)
    );

    if (!businessCase) {
      console.warn("[assembleBusinessCase] blocked: businessCase not found", {  businessCaseId,
    ids: businessCases.map(b => b.business_case_id),
  });
      dispatch(failure("Selected business case not found."));
      return;
    }

    if (!organization?.expanded_periods) {
       console.warn("[assembleBusinessCase] blocked: org expanded_periods missing", organization);
       console.trace("[assembleBusinessCase] dispatched from");
      dispatch(failure("Organization expansion settings not loaded yet."));
      return;
    }

    dispatch(request());

    try {
      // single canonical path
      const result = await dispatch(
        assembleBaseline({
          bc: businessCase,
          periods,
          accountStructure: calculationAccountStructure,
          expandedPeriods: organization.expanded_periods,
          allocations,
          entities,
          force,
        })
      );

      // result.data is normalized by assemblyActions.normalizeAssemblyResponse
      const raw = result?.data ?? {};
      const assembled = raw?.assembledBusinessCase ?? raw;

      // keep existing reducer contract (compiledBusinessCase shape)
      const scenario = assembled?.scenario || {};
      const valuesObj = scenario.values || {};
      const rollupObj = scenario.roll_up || {};

      const compiledBusinessCase = { ...businessCase, ...assembled };
  console.log('[bc id]', businessCase.business_case_id, '->', compiledBusinessCase.business_case_id);

      const accountNumberToId = buildAccountNumberToId(displayAccountStructure);
      compiledBusinessCase.scenario = {
        ...compiledBusinessCase.scenario,
        values: new Map(Object.entries(valuesObj)),
        originalValues: new Map(Object.entries(valuesObj)),
        roll_up: new Map(Object.entries(rollupObj)),
        compiled: true,
      };

      compiledBusinessCase.scenario.valuesById =
        remapToId(compiledBusinessCase.scenario.values, accountNumberToId, normalizeAccNo);

      compiledBusinessCase.scenario.originalValuesById =
        remapToId(compiledBusinessCase.scenario.originalValues, accountNumberToId, normalizeAccNo);

      compiledBusinessCase.scenario.rollUpById =
        remapToId(compiledBusinessCase.scenario.roll_up, accountNumberToId, normalizeAccNo);

      dispatch(success(compiledBusinessCase));
      return compiledBusinessCase;
    } catch (error) {
      console.error(error);
      dispatch(
        failure(
          error?.response?.data?.toString?.() ??
            error?.message ??
            "Assemble business case failed."
        )
      );
    }

    function request() {
      return { type: businessConstants.ASSEMBLE_BUSINESS_CASE_REQUEST };
    }
    function success(assembledBusinessCase) {
      return {
        type: businessConstants.ASSEMBLE_BUSINESS_CASE_SUCCESS,
        assembledBusinessCase,
      };
    }
    function failure(error) {
      return {
        type: businessConstants.ASSEMBLE_BUSINESS_CASE_FAILURE,
        error,
      };
    }
  };
}

function clearAssembledBusinessCase(businessCaseId) {
    return {
        type: businessConstants.CLEAR_ASSEMBLED_BUSINESS_CASE,
        payload: businessCaseId,
    };
}

function assembleBusinessCaseTotalsBatch(businessCases, opts = {}) {
  const { force = false } = opts;

  return async (dispatch, getState) => {
    const state = getState();

    const displayAccountStructure =
      state.accounts.accountStructure;

    const calculationAccountStructure =
      buildCalculationAccountStructure(
        displayAccountStructure,
      );
    const organization = state.organization.organization;
    const expandedPeriods = organization?.expanded_periods;
    const allocations = state.allocations.allocations ?? [];

    // Guard: nothing to do
    if (!Array.isArray(businessCases) || businessCases.length === 0) {
      dispatch({
        type: businessConstants.ASSEMBLE_BUSINESS_CASES_SUCCESS,
        assembledBusinessCases: [],
      });
      return [];
    }

    // Guard: required data not ready (prevents sending bad requests)
    if (!displayAccountStructure) {
      dispatch({
        type: businessConstants.ASSEMBLE_BUSINESS_CASES_FAILURE,
        error: "Account structure not loaded yet.",
      });
      return [];
    }

    if (!expandedPeriods) {
      dispatch({
        type: businessConstants.ASSEMBLE_BUSINESS_CASES_FAILURE,
        error: "Organization expanded periods not loaded yet.",
      });
      return [];
    }

    // Now we can start "loading"
    dispatch({ type: businessConstants.ASSEMBLE_BUSINESS_CASES_REQUEST });

    try {

      const response = await axios.post(
        "/assemble-business-case-totals-batch",
        {
          businessCases,
          accountStructure: calculationAccountStructure,
          expandedPeriods,
          allocations,
          force,
        },
        { headers: { "X-Debug-Caller": "assembleBusinessCaseTotalsBatch" } }
      );

      if (response.status !== 200) {
        const msg = response.data?.toString?.() ?? "Failed assembling business case totals";
        dispatch({ type: businessConstants.ASSEMBLE_BUSINESS_CASES_FAILURE, error: msg });
        return [];
      }

      const accountNumberToId = buildAccountNumberToId(displayAccountStructure);
      const toMap = (obj) => new Map(Object.entries(obj || {}));

      const assembledBusinessCases = (response.data || []).map((bcData) => {
        const assembledBusinessCase = { ...bcData };
        assembledBusinessCase.business_case_id = Number(assembledBusinessCase.business_case_id);
        assembledBusinessCase.assembledCase = assembledBusinessCase.assembledCase || {};

        if (assembledBusinessCase.error) {
          assembledBusinessCase.assembledCase.values = new Map();
          assembledBusinessCase.assembledCase.valuesById = new Map();
          return assembledBusinessCase;
        }

        const valuesByNumber = toMap(bcData.assembledCase?.values);
        assembledBusinessCase.assembledCase.values = valuesByNumber;
        assembledBusinessCase.assembledCase.valuesById =
          remapToId(valuesByNumber, accountNumberToId, normalizeAccNo);

        return assembledBusinessCase;
      });

      dispatch({
        type: businessConstants.ASSEMBLE_BUSINESS_CASES_SUCCESS,
        assembledBusinessCases,
      });

      return assembledBusinessCases;
    } catch (error) {
      console.error("assembleBusinessCaseTotalsBatch failed", error);
      const msg =
        error?.response?.data?.toString?.() ??
        error?.message ??
        "Business case totals not yet initialized.";

      dispatch({ type: businessConstants.ASSEMBLE_BUSINESS_CASES_FAILURE, error: msg });
      return [];
    }
  };
}

 function checkBcWarmStatus(businessCaseId) {
    return async (dispatch) => {
        try {
            const { data } = await axios.get(`/bc-warm-status/${businessCaseId}`);
            dispatch({
                type: businessConstants.BC_WARM_STATUS_UPDATED,
                businessCaseId,
                status: data.status,
            });
            return data.status;
        } catch (err) {
            console.error('Failed to fetch BC warm status', err);
        }
    };
}

function checkAllBcWarmStatuses() {
    return async (dispatch) => {
        try {
            const { data } = await axios.get('/bc-warm-status');
            dispatch({
                type: businessConstants.BC_WARM_STATUSES_UPDATED,
                statuses: data.statuses,
            });
        } catch (err) {
            console.error('Failed to fetch BC warm statuses', err);
        }
    };
}
